// Racine de l'application : choisit l'écran (accueil, connexion, création du héros, jeu), superpose
// les couches permanentes (braises, dés, notifications) et le bandeau de reconnexion.
import "./styles/theme.css";
import "./main.css";
import { render } from "preact";
import { useEffect, useState } from "preact/hooks";
import type { ClientView } from "../shared/types";
import { lastSession, listSessions } from "./api";
import type { Session } from "./api";
import { connect, disconnect, useAppState } from "./store";
import type { AppState } from "./store";
import { Campfire, Home, readConnexionLink, readInviteCode } from "./screens/Home";
import { Game } from "./screens/Game";
import { CharacterCreation } from "./screens/CharacterCreation";
import { ChatGptLink } from "./screens/ChatGptLink";
import { Embers } from "./components/Embers";
import { DiceOverlay } from "./components/DiceOverlay";
import { Toasts } from "./components/Toasts";
import { MusicToggle } from "./components/MusicToggle";
import { initMusic } from "./music";
import { initNative } from "./native";

type ConnectionState = AppState["connection"];

/**
 * Au lancement : reprend le dernier monde. Un lien de connexion a priorité (l'accueil retrouve le héros du lien),
 * puis un lien d'invitation ; vers un monde inconnu, l'accueil s'ouvre avec le code prérempli.
 */
function resumeSavedWorld(): void {
  if (readConnexionLink()) return;
  const invited = readInviteCode();
  if (invited) {
    const known = listSessions().find((s) => s.code === invited);
    if (known) connect(known);
    return;
  }
  const last = lastSession();
  if (last) connect(last);
}

function registerServiceWorker(): void {
  try {
    void navigator.serviceWorker?.register("/sw.js").catch(() => undefined);
  } catch {
    // Pas de service worker (navigation privée, origine non sécurisée) : l'application reste utilisable.
  }
}

interface ConnectingProps {
  session: Session;
  connection: ConnectionState;
  error: string | null;
}

/** Écran affiché le temps que le monde envoie sa première vue. */
function Connecting({ session, connection, error }: ConnectingProps) {
  let text = `Retour au monde « ${session.worldName} »`;
  if (error) text = error;
  else if (connection === "deconnecte") text = "Le réseau hésite. Nouvel essai en cours…";
  return (
    <main class="app-connecting">
      <Campfire size={150} />
      <h1 class="app-connecting-title">{error ? "Le feu ne prend pas" : "Le feu s’allume…"}</h1>
      <p class="app-connecting-text">{text}</p>
      <div class="app-connecting-actions">
        {error && (
          <button type="button" class="btn btn-primary" onClick={() => connect(session)}>
            Réessayer
          </button>
        )}
        <button type="button" class="btn btn-ghost" onClick={() => disconnect()}>
          Revenir à l’accueil
        </button>
      </div>
    </main>
  );
}

function currentScreen(session: Session | null, view: ClientView | null, connection: ConnectionState, error: string | null) {
  if (!session) return <Home />;
  if (!view) return <Connecting session={session} connection={connection} error={error} />;
  // monde au ChatGPT de son créateur, jamais connecté (ou déconnecté) : l'hôte le connecte d'abord
  if (view.chatgpt && view.chatgpt.model === null && view.me.isHost) return <ChatGptLink view={view} />;
  if (!view.me.hasCharacter) return <CharacterCreation mode="create" />;
  return <Game />;
}

function App() {
  const { session, view, connection, error } = useAppState();
  const code = session?.code ?? null;
  const [connectedCode, setConnectedCode] = useState<string | null>(null);

  useEffect(() => {
    // hors d'un monde (accueil, monde quitté ou oublié), la mémoire de connexion repart à zéro
    if (code === null) setConnectedCode(null);
    else if (connection === "connecte") setConnectedCode(code);
  }, [code, connection]);

  // Le bandeau ne concerne qu'un monde déjà ouvert pendant cette visite, pas l'accueil.
  const reconnecting = code !== null && connectedCode === code && connection !== "connecte";

  // Décale les notifications sous le bandeau (voir Toasts.css).
  useEffect(() => {
    document.documentElement.toggleAttribute("data-reconnecting", reconnecting);
  }, [reconnecting]);

  return (
    <>
      <Embers />
      {reconnecting && <div class="app-reconnect" role="status">Reconnexion…</div>}
      {currentScreen(session, view, connection, error)}
      {!view?.me.hasCharacter && <MusicToggle variant="flottant" />}
      <DiceOverlay />
      <Toasts />
    </>
  );
}

resumeSavedWorld();
registerServiceWorker();
initMusic();
initNative();
const root = document.getElementById("app");
if (!root) throw new Error("Élément #app introuvable dans index.html");
root.replaceChildren();
render(<App />, root);
